import React, { useEffect, useContext } from "react";
import { usePlaidLink, PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import Context from "../Context";
import AddButtonWithArrow from "../commonCom/AddButtonWithArrow";
import { PaymentService, PaymentMethodTypesEnum } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { GetImages } from "../../../utils/GetImages";

const Link = () => {
  const { linkToken, isPaymentInitiation, dispatch, plaidEntityConfig, plaidButtonLabel, plaidReconnectPaymentMethod } =
    useContext(Context);

  const onSuccess = React.useCallback(
    (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      const linkedAccount = metadata.accounts?.[0];
      const institutionName = metadata.institution?.name ?? null;
      const accountName = linkedAccount?.name || institutionName || "Plaid Account";

      const plaidAccountDetails = {
        accountName,
        accountNumberMask: linkedAccount?.mask ?? null,
        accountId: linkedAccount?.id ?? null,
        itemId: metadata.institution?.institution_id ?? null,
        institutionName,
        publicAccessToken: public_token,
      };

      const savePlaidPaymentMethod = async () => {
        const isReconnect = !!plaidReconnectPaymentMethod;

        if (!plaidEntityConfig && !isReconnect) {
          dispatch({
            type: "SET_STATE",
            state: {
              isItemAccess: true,
              plaidAccountDetails,
              linkSuccess: true,
            },
          });
          return;
        }

        try {
          const plaidPayload = {
            accountName,
            paymentMethodTypeId: PaymentMethodTypesEnum.Plaid,
            plaidPaymentMethod: {
              publicAccessToken: public_token,
              accountId: linkedAccount?.id ?? null,
              accountNumberMask: linkedAccount?.mask ?? null,
              itemId: metadata.institution?.institution_id ?? null,
              institutionName,
            },
            achPaymentMethod: undefined,
            creditCardPaymentMethod: undefined,
          };

          const payload = isReconnect
            ? {
                ...plaidReconnectPaymentMethod,
                ...plaidPayload,
                entityTypeId: plaidReconnectPaymentMethod.entityTypeId,
                entityId: plaidReconnectPaymentMethod.entityId,
              }
            : {
                ...plaidPayload,
                entityTypeId: plaidEntityConfig!.entityTypeId,
                entityId: plaidEntityConfig!.entityId,
              };

          await apiHelper(
            isReconnect ? PaymentService.putApiPaymentMethod : PaymentService.postApiPaymentMethod,
            {
              title: isReconnect ? "Plaid Account Reconnected" : "Plaid Account Added",
              successMessage: isReconnect ? "Plaid account reconnected successfully" : "Plaid account linked successfully",
              failureMessage: isReconnect ? "Unable to reconnect Plaid account" : "Unable to add Plaid account",
              showNotification: true,
              successIcon: (
                <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />
              ),
            },
            payload
          );

          dispatch({
            type: "SET_STATE",
            state: {
              itemId: plaidAccountDetails.itemId,
              accessToken: public_token,
              isItemAccess: true,
              plaidAccountDetails,
              linkSuccess: true,
            },
          });
        } catch (error) {
          console.error("Error saving Plaid payment method:", error);
          dispatch({
            type: "SET_STATE",
            state: {
              isItemAccess: false,
              plaidAccountDetails: null,
              linkSuccess: true,
            },
          });
        }
      };

      if (isPaymentInitiation) {
        dispatch({ type: "SET_STATE", state: { isItemAccess: false, linkSuccess: true } });
      } else {
        savePlaidPaymentMethod();
      }
    },
    [dispatch, isPaymentInitiation, plaidEntityConfig, plaidReconnectPaymentMethod]
  );

  let isOauth = false;
  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken!,
    onSuccess,
  };

  if (window.location.href.includes("?oauth_state_id=")) {
    // TODO: figure out how to delete this ts-ignore
    // @ts-ignore
    config.receivedRedirectUri = window.location.href;
    isOauth = true;
  }

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (isOauth && ready) {
      open();
    }
  }, [ready, open, isOauth]);

  return (
    <AddButtonWithArrow
      buttonname={plaidButtonLabel || "Connect Now"}
      onClick={(e) => {
        e.preventDefault();
        open();
      }}
      disabled={!ready}
    />
  );
};

Link.displayName = "Link";

export default Link;
